import React, { useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import type { EmbeddedComment } from '../../../../../../../common/ui/embedded-comments';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { SearchResultContextFooter } from '../../../../../../../common/ui/search-result-context-footer';
import { StatusLozengeButton } from '../../../../../../../common/ui/status-lozenge-button';
import { enrichExcerpt } from '../../../../../../../common/utils/search-result-utils';
import type { searchGraphCase_AtlassianSearchPage$key } from './__generated__/searchGraphCase_AtlassianSearchPage.graphql';
import { messages } from './messages';

const styles = cssMap({
	caseContainer: {
		width: '100%',
	},
	resultWrapper: {
		flex: '1',
		minWidth: '0px',
	},
	content: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

export const SearchGraphCase = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchGraphResultProps<searchGraphCase_AtlassianSearchPage$key>): React.JSX.Element => {
	const { formatMessage, formatDate } = useIntl();

	const data = useFragment(
		graphql`
			fragment searchGraphCase_AtlassianSearchPage on SearchResultGraphDocument
			@argumentDefinitions(includeCaseEmbeddedComments: { type: "Boolean", defaultValue: false }) {
				id
				url
				title
				lastModifiedDate
				excerpt
				entity {
					... on ExternalCase {
						displayNumber
						status
						closedAt
						lastUpdated
						owners {
							thirdPartyUser {
								name
							}
							user {
								name
							}
						}
						createdBy {
							thirdPartyUser {
								name
							}
						}
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalCustomerOrg {
								id
								displayName
							}
						}
					}
				}
				linkedEntities @include(if: $includeCaseEmbeddedComments) {
					id
					parentId
					excerpt
					url
					entity {
						... on ExternalComment {
							displayName
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { id, url, title, lastModifiedDate, excerpt, entity, linkedEntities } = data;

	const isCaseEmbeddedCommentsEnabled = fg('rovo_fps_case_embedded_comments');
	const embeddedComments = useMemo<EmbeddedComment[]>(() => {
		if (!isCaseEmbeddedCommentsEnabled || !linkedEntities?.length) {
			return [];
		}

		return linkedEntities
			.filter((linkedEntity) => linkedEntity.excerpt)
			.map(
				(linkedEntity): EmbeddedComment => ({
					id: linkedEntity.id,
					parentId: linkedEntity.parentId ?? null,
					excerpt: linkedEntity.excerpt ?? null,
					url: linkedEntity.url,
					displayName: linkedEntity.entity?.displayName ?? null,
				}),
			);
	}, [isCaseEmbeddedCommentsEnabled, linkedEntities]);

	const ownerName =
		entity?.owners?.[0]?.thirdPartyUser?.name ??
		entity?.owners?.[0]?.user?.name ??
		entity?.createdBy?.thirdPartyUser?.name;

	const lastModifiedComponent = (
		<LastModified lastModified={entity?.lastUpdated ?? lastModifiedDate} />
	);

	const description = <DescriptionBody description={enrichExcerpt(excerpt)} />;

	const statusText = entity?.status ?? '';

	const closedAtValue = entity?.closedAt ? new Date(entity.closedAt) : null;
	const formattedClosedAt = closedAtValue
		? formatDate(closedAtValue, {
				month: 'long',
				day: 'numeric',
				year: 'numeric',
			})
		: '';
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const closedAtMessage =
		closedAtValue && closedAtValue >= today ? messages.closesOn : messages.closedOn;

	const closedAtPopupContent = closedAtValue ? (
		<Box xcss={styles.content}>
			<FormattedMessage {...closedAtMessage} values={{ date: formattedClosedAt }} />
		</Box>
	) : undefined;

	const containerName = entity?.container?.displayName;

	const headerMetadataParts = [
		formatMessage(messages.caseLabel),
		containerName,
		['owner', ownerName],
		['last-modified', lastModifiedComponent],
	];

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const enrichedTitle = enrichExcerpt(title);

	const footerComponent = isCaseEmbeddedCommentsEnabled ? (
		<SearchResultContextFooter
			embeddedComments={embeddedComments}
			isEmbeddedCommentsEnabled
			linkedResults={linkedResults}
			isMentionedInEnabled
			permissionLevel={null}
			isNotPublic={false}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			position={position}
			id={id}
			url={url}
			enrichedTitle={enrichedTitle}
			product={product}
			selectedProduct={selectedProduct}
			primaryProduct={primaryProduct}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
		/>
	) : (
		<MentionedByLinkedResults
			productConfigs={productConfigs}
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	);

	return (
		<Flex gap="space.100" alignItems="start" role="listitem" xcss={styles.caseContainer}>
			<Box xcss={styles.resultWrapper}>
				<SearchResult
					productConfigs={productConfigs}
					id={id}
					product={product}
					productSelected={!!selectedProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					icon={icon}
					description={description}
					headerMetadataParts={headerMetadataParts}
					title={
						entity?.displayNumber ? (
							<>
								<Box as="span" color="color.link">
									{entity.displayNumber}
								</Box>{' '}
								{enrichedTitle}
							</>
						) : (
							enrichedTitle
						)
					}
					role="none"
					url={url}
					isThirdParty
					cloudId={cloudId}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					position={position}
					partial={data}
					appMode={appMode}
					footer={footerComponent}
					hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			</Box>
			{statusText && (
				<StatusLozengeButton statusText={statusText} popupContent={closedAtPopupContent} />
			)}
		</Flex>
	);
};
